import axios from "axios";

// =====================================================
// AUTOMATIC ENVIRONMENT DETECTION
// =====================================================
// - localhost:3000 → Uses local backend (localhost:5025)
// - Production URL → Uses Render backend
// =====================================================

const getApiUrl = () => {
  const hostname = window.location.hostname;
  
  // Local development
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    console.log("🔧 Environment: LOCAL - Using localhost:5025");
    return "http://localhost:5025/api";
  }
  
  // Production (Render, Vercel, Netlify, or any deployed URL)
  console.log("🌐 Environment: PRODUCTION - Using AWS backend");
  return "/api";
};

const API_URL = getApiUrl();

// Log the API URL on startup
console.log("API URL:", API_URL);

// =====================================================
// AXIOS INSTANCE WITH INTERCEPTORS
// =====================================================

const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Add auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors (token expired)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - redirect to login
      const currentPath = window.location.pathname;
      
      // Don't redirect if already on login page
      if (!currentPath.includes("login")) {
        console.warn("401 Unauthorized - Redirecting to login");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.location.href = "/user-login";
      }
    }
    return Promise.reject(error);
  }
);

// =====================================================
// AUTH API
// =====================================================

export const authAPI = {
  login: async (email, password) => {
    const response = await api.post("/Auth/login", { email, password });
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
    }
    return response.data;
  },

  register: async (fullName, email, password) => {
    const response = await api.post("/Auth/register", { fullName, email, password });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/user-login";
  },

  getCurrentUser: () => {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem("token");
  },
};

// =====================================================
// ADMIN API
// =====================================================

export const adminAPI = {
  getDashboard: async () => {
    const response = await api.get("/Admin/dashboard");
    return response.data;
  },

  getAllUsers: async () => {
    const response = await api.get("/Admin/users");
    return response.data;
  },

  updateUserStatus: async (userId, isActive) => {
    const response = await api.put(`/Admin/users/${userId}/status`, { isActive });
    return response.data;
  },

  promoteToAgent: async (userId) => {
    const response = await api.put(`/Admin/users/${userId}/promote-agent`);
    return response.data;
  },

  demoteFromAgent: async (userId) => {
    const response = await api.put(`/Admin/users/${userId}/demote-agent`);
    return response.data;
  },
};

// =====================================================
// WALLET API
// =====================================================

export const walletAPI = {
  getMyWallet: async () => {
    const response = await api.get("/Wallet/my-wallet");
    return response.data;
  },

  getBalance: async () => {
    const response = await api.get("/Wallet/balance");
    return response.data;
  },

  transfer: async (recipientEmail, amount, description) => {
    const response = await api.post("/Wallet/transfer", {
      recipientEmail,
      amount,
      description,
    });
    return response.data;
  },

  getTransactions: async (page = 1, pageSize = 20) => {
    const response = await api.get(`/Wallet/transactions?page=${page}&pageSize=${pageSize}`);
    return response.data;
  },

  deposit: async (amount, description) => {
    const response = await api.post("/Wallet/deposit", { amount, description });
    return response.data;
  },

  withdraw: async (amount, description) => {
    const response = await api.post("/Wallet/withdraw", { amount, description });
    return response.data;
  },
};

// =====================================================
// TRANSACTION API
// =====================================================

export const transactionAPI = {
  getHistory: async (limit = 50) => {
    const response = await api.get(`/Transactions/history?limit=${limit}`);
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/Transactions/${id}`);
    return response.data;
  },

  getReceipt: async (id) => {
    const response = await api.get(`/Transactions/${id}/receipt`, {
      responseType: "blob",
    });
    return response.data;
  },

  getAll: async (params = {}) => {
    const response = await api.get("/Transactions/all", { params });
    return response.data;
  },

  getSummary: async (startDate, endDate) => {
    const response = await api.get("/Transactions/summary", {
      params: { startDate, endDate },
    });
    return response.data;
  },
};

// =====================================================
// NOTIFICATION API
// =====================================================

export const notificationAPI = {
  getAll: async () => {
    const response = await api.get("/Notifications");
    return response.data;
  },

  getUnread: async () => {
    const response = await api.get("/Notifications/unread");
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await api.get("/Notifications/unread-count");
    return response.data;
  },

  markAsRead: async (id) => {
    const response = await api.put(`/Notifications/${id}/read`);
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await api.put("/Notifications/read-all");
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/Notifications/${id}`);
    return response.data;
  },
};

// =====================================================
// REPORTS API
// =====================================================

export const reportsAPI = {
  getTransactionReport: async (params = {}) => {
    const response = await api.get("/Reports/transactions", { params });
    return response.data;
  },

  getUserReport: async (params = {}) => {
    const response = await api.get("/Reports/users", { params });
    return response.data;
  },

  getWalletReport: async (params = {}) => {
    const response = await api.get("/Reports/wallets", { params });
    return response.data;
  },

  getSummaryReport: async (params = {}) => {
    const response = await api.get("/Reports/summary", { params });
    return response.data;
  },

  getDailyReport: async (date) => {
    const response = await api.get("/Reports/daily", { params: { date } });
    return response.data;
  },

  exportTransactions: async (params = {}) => {
    const response = await api.get("/Reports/transactions/export", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  getAgentReport: async (params = {}) => {
    const response = await api.get("/Reports/agents", { params });
    return response.data;
  },
};

// =====================================================
// AGENT API
// =====================================================

export const agentAPI = {
  deposit: async (customerAccountNumber, amount, description) => {
    const response = await api.post("/Wallet/agent/deposit", {
      customerAccountNumber,
      amount,
      description,
    });
    return response.data;
  },

  withdrawal: async (customerAccountNumber, amount, description) => {
    const response = await api.post("/Wallet/agent/withdrawal", {
      customerAccountNumber,
      amount,
      description,
    });
    return response.data;
  },

  searchCustomer: async (accountNumber, email) => {
    const response = await api.get("/Wallet/agent/search-customer", {
      params: { accountNumber, email },
    });
    return response.data;
  },

  getFloat: async () => {
    const response = await api.get("/Wallet/agent/float");
    return response.data;
  },

  getDailySummary: async () => {
    const response = await api.get("/Wallet/agent/daily-summary");
    return response.data;
  },
};

// =====================================================
// KYC API
// =====================================================

export const kycAPI = {
  getMyKyc: async () => {
    const response = await api.get("/Kyc/my-kyc");
    return response.data;
  },

  submitKyc: async (data) => {
    const response = await api.post("/Kyc/submit", data);
    return response.data;
  },

  uploadDocument: async (formData) => {
    const response = await api.post("/Kyc/upload-document", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },

  // Admin
  getPending: async () => {
    const response = await api.get("/Kyc/pending");
    return response.data;
  },

  approve: async (userId) => {
    const response = await api.post(`/Kyc/${userId}/approve`);
    return response.data;
  },

  reject: async (userId, reason) => {
    const response = await api.post(`/Kyc/${userId}/reject`, { reason });
    return response.data;
  },
};

// =====================================================
// FEE API
// =====================================================

export const feeAPI = {
  getAll: async () => {
    const response = await api.get("/Fees");
    return response.data;
  },

  getPreview: async (transactionType, amount) => {
    const response = await api.get("/Wallet/fee-preview", {
      params: { transactionType, amount },
    });
    return response.data;
  },

  create: async (data) => {
    const response = await api.post("/Fees", data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/Fees/${id}`, data);
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/Fees/${id}`);
    return response.data;
  },
};

// =====================================================
// EXPORT DEFAULT INSTANCE
// =====================================================

export default api;
